export const EV_MONTHLY_PRICING_EXPERIMENT_ID = "pricing_ev_monthly_price_test";
export const DIAMOND_SNIPER_MONTHLY_PRICING_EXPERIMENT_ID = "pricing_diamond_sniper_monthly_price_test";
export const EV_MONTHLY_PRICING_CONTROL_VARIANT = "control";
export const EV_MONTHLY_PRICING_DEFAULT_VARIANT = "variant_a";

export const PRICING_EXPERIMENT_QUERY_KEYS = {
  id: "exp_id",
  variant: "exp_variant",
  priceId: "exp_price_id",
  exposure: "exp_src",
} as const;

const PRICING_EXPERIMENT_VALUE_MAX_LENGTH = 100;
const PAYLOAD_PRICE_ID_KEY = "priceId";
export const PRICING_EXPERIMENT_EXPOSURE_SOURCE = "pricing_page_viewed";

const DEFAULT_EV_MONTHLY_CONTROL_PRICE_ID = "price_1SXocCGrgdPJmrXHYzrnVO6G";
const DEFAULT_EV_MONTHLY_VARIANT_PRICE_ID = "price_1T957LGrgdPJmrXH8P3kQfHH";

const DEFAULT_DIAMOND_MONTHLY_CONTROL_PRICE_ID = "price_1TlvHaGrgdPJmrXHSN0S0olM";
const DEFAULT_DIAMOND_MONTHLY_VARIANT_PRICE_ID = "price_1TqyQoGrgdPJmrXHrLoPHRg6";

export interface PricingExperimentMetadata {
  pricing_experiment_id: string;
  pricing_experiment_variant: string;
  pricing_experiment_price_id: string;
}

type FeatureFlagValue = string | boolean | null;

export interface ResolveEvMonthlyPricingExperimentOptions {
  controlPriceId: string;
  controlPrice: number;
  allowedPriceIds: Iterable<string>;
  featureFlagValue: FeatureFlagValue;
  featureFlagPayload: Record<string, unknown> | null;
  priceById?: ReadonlyMap<string, number>;
  experimentId?: string;
  payloadPriceIdKey?: string;
  pinScope?: string;
}

export interface EvMonthlyPricingExperimentResolution {
  checkoutPriceId: string;
  displayPrice: number;
  assignedVariant: string | null;
  metadata: PricingExperimentMetadata | null;
  fallbackReason:
    | "missing_payload_price_id"
    | "unknown_payload_price_id"
    | null;
}

export interface SanitizeAndValidatePricingExperimentMetadataOptions {
  expectedPriceId?: string | null;
  requireComplete?: boolean;
}

export interface ExperimentTierLike {
  id?: string;
  kind?: string;
  priceId: string;
  productType: string;
  billingPeriod: string;
  price: number;
}

function resolvePriceFromMap(
  priceById: ReadonlyMap<string, number> | undefined,
  normalizedPriceId: string,
): number | null {
  if (!priceById) return null;

  const exactMatch = priceById.get(normalizedPriceId);
  if (exactMatch !== undefined) return exactMatch;

  for (const [candidatePriceId, candidatePrice] of priceById) {
    if (sanitizePricingExperimentValue(candidatePriceId) === normalizedPriceId) {
      return candidatePrice;
    }
  }

  return null;
}

function normalizeVariant(value: FeatureFlagValue): string | null {
  if (typeof value === "boolean") {
    return value
      ? EV_MONTHLY_PRICING_DEFAULT_VARIANT
      : EV_MONTHLY_PRICING_CONTROL_VARIANT;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;

  if (
    normalized === "control" ||
    normalized === "false" ||
    normalized === "0"
  ) {
    return EV_MONTHLY_PRICING_CONTROL_VARIANT;
  }

  return normalized;
}

export function sanitizePricingExperimentValue(
  value: unknown,
  maxLength: number = PRICING_EXPERIMENT_VALUE_MAX_LENGTH,
): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, maxLength);
}

export function sanitizePricingExperimentMetadata(
  input:
    | Partial<
        Record<keyof PricingExperimentMetadata, unknown>
      >
    | null
    | undefined,
): Partial<PricingExperimentMetadata> {
  if (!input) return {};

  const pricing_experiment_id = sanitizePricingExperimentValue(
    input.pricing_experiment_id,
  );
  const pricing_experiment_variant = sanitizePricingExperimentValue(
    input.pricing_experiment_variant,
  );
  const pricing_experiment_price_id = sanitizePricingExperimentValue(
    input.pricing_experiment_price_id,
  );

  return {
    ...(pricing_experiment_id
      ? { pricing_experiment_id }
      : {}),
    ...(pricing_experiment_variant
      ? { pricing_experiment_variant }
      : {}),
    ...(pricing_experiment_price_id
      ? { pricing_experiment_price_id }
      : {}),
  };
}

export function isCompletePricingExperimentMetadata(
  metadata: Partial<PricingExperimentMetadata> | null | undefined,
): metadata is PricingExperimentMetadata {
  const sanitized = sanitizePricingExperimentMetadata(metadata);
  return Boolean(
    sanitized.pricing_experiment_id &&
      sanitized.pricing_experiment_variant &&
      sanitized.pricing_experiment_price_id,
  );
}

export function isEvMonthlyExperimentPriceId(
  priceId: string | null | undefined,
): boolean {
  const normalizedPriceId = sanitizePricingExperimentValue(priceId);
  if (!normalizedPriceId) return false;

  const knownExperimentPriceIds = new Set(
    [
      process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_MONTHLY_PRICE_ID,
      process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_MONTHLY_EXPERIMENT_PRICE_ID,
      DEFAULT_EV_MONTHLY_CONTROL_PRICE_ID,
      DEFAULT_EV_MONTHLY_VARIANT_PRICE_ID,
    ]
      .map((candidate) => sanitizePricingExperimentValue(candidate))
      .filter((candidate): candidate is string => Boolean(candidate)),
  );

  return knownExperimentPriceIds.has(normalizedPriceId);
}

export function isDiamondMonthlyExperimentPriceId(
  priceId: string | null | undefined,
): boolean {
  const normalizedPriceId = sanitizePricingExperimentValue(priceId);
  if (!normalizedPriceId) return false;

  const knownExperimentPriceIds = new Set(
    [
      process.env.NEXT_PUBLIC_STRIPE_DIAMOND_MONTHLY_PRICE_ID,
      process.env.NEXT_PUBLIC_STRIPE_DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
      DEFAULT_DIAMOND_MONTHLY_CONTROL_PRICE_ID,
      DEFAULT_DIAMOND_MONTHLY_VARIANT_PRICE_ID,
    ]
      .map((candidate) => sanitizePricingExperimentValue(candidate))
      .filter((candidate): candidate is string => Boolean(candidate)),
  );

  return knownExperimentPriceIds.has(normalizedPriceId);
}

export function isAnyMonthlyExperimentPriceId(
  priceId: string | null | undefined,
): boolean {
  return (
    isEvMonthlyExperimentPriceId(priceId) ||
    isDiamondMonthlyExperimentPriceId(priceId)
  );
}

export function sanitizeAndValidatePricingExperimentMetadata(
  input:
    | Partial<Record<keyof PricingExperimentMetadata, unknown>>
    | null
    | undefined,
  options: SanitizeAndValidatePricingExperimentMetadataOptions = {},
): Partial<PricingExperimentMetadata> {
  const sanitized = sanitizePricingExperimentMetadata(input);
  const expectedPriceId = sanitizePricingExperimentValue(options.expectedPriceId);

  if (
    expectedPriceId &&
    sanitized.pricing_experiment_price_id !== expectedPriceId
  ) {
    return {};
  }

  if (options.requireComplete) {
    if (
      !sanitized.pricing_experiment_id ||
      !sanitized.pricing_experiment_variant ||
      !sanitized.pricing_experiment_price_id
    ) {
      return {};
    }
  }

  return sanitized;
}

export function buildPricingExperimentFeatureFlagProperties(
  metadata: Partial<PricingExperimentMetadata> | null | undefined,
): Record<string, string> {
  const sanitizedMetadata = sanitizePricingExperimentMetadata(metadata);
  const experimentId = sanitizedMetadata.pricing_experiment_id;
  const experimentVariant = sanitizedMetadata.pricing_experiment_variant;

  if (!experimentId || !experimentVariant) {
    return {};
  }

  return {
    [`$feature/${experimentId}`]: experimentVariant,
  };
}

export function findCompatibleExperimentTier<T extends ExperimentTierLike>(
  selectedTier:
    | Pick<ExperimentTierLike, "id" | "kind" | "productType" | "billingPeriod">
    | null
    | undefined,
  allTiers: Iterable<T>,
  experimentPriceId: string | null | undefined,
): T | null {
  const normalizedExperimentPriceId = sanitizePricingExperimentValue(
    experimentPriceId,
  );
  if (!selectedTier || !normalizedExperimentPriceId) {
    return null;
  }

  for (const tier of allTiers) {
    const normalizedTierPriceId = sanitizePricingExperimentValue(tier.priceId);
    if (normalizedTierPriceId !== normalizedExperimentPriceId) continue;
    if (tier.productType !== selectedTier.productType) continue;
    if (tier.billingPeriod !== selectedTier.billingPeriod) continue;
    if (tier.kind && selectedTier.kind && tier.kind !== selectedTier.kind) continue;
    if (tier.id && selectedTier.id && tier.id !== selectedTier.id) continue;
    return tier;
  }

  return null;
}

export function appendPricingExperimentQueryParams(
  params: URLSearchParams,
  metadata: Partial<PricingExperimentMetadata> | null | undefined,
): void {
  const sanitizedMetadata = sanitizePricingExperimentMetadata(metadata);
  if (!sanitizedMetadata) return;

  if (sanitizedMetadata.pricing_experiment_id) {
    params.set(
      PRICING_EXPERIMENT_QUERY_KEYS.id,
      sanitizedMetadata.pricing_experiment_id,
    );
  }
  if (sanitizedMetadata.pricing_experiment_variant) {
    params.set(
      PRICING_EXPERIMENT_QUERY_KEYS.variant,
      sanitizedMetadata.pricing_experiment_variant,
    );
  }
  if (sanitizedMetadata.pricing_experiment_price_id) {
    params.set(
      PRICING_EXPERIMENT_QUERY_KEYS.priceId,
      sanitizedMetadata.pricing_experiment_price_id,
    );
  }

  if (isCompletePricingExperimentMetadata(sanitizedMetadata)) {
    params.set(
      PRICING_EXPERIMENT_QUERY_KEYS.exposure,
      PRICING_EXPERIMENT_EXPOSURE_SOURCE,
    );
  }
}

type SearchParamsLike = { get: (key: string) => string | null } | null | undefined;

export function readPricingExperimentQueryParams(
  searchParams: SearchParamsLike,
): Partial<PricingExperimentMetadata> {
  if (!searchParams) return {};

  return sanitizePricingExperimentMetadata({
    pricing_experiment_id: searchParams.get(PRICING_EXPERIMENT_QUERY_KEYS.id),
    pricing_experiment_variant: searchParams.get(
      PRICING_EXPERIMENT_QUERY_KEYS.variant,
    ),
    pricing_experiment_price_id: searchParams.get(
      PRICING_EXPERIMENT_QUERY_KEYS.priceId,
    ),
  });
}

export function hasPricingExperimentExposureSource(
  searchParams: SearchParamsLike,
): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get(PRICING_EXPERIMENT_QUERY_KEYS.exposure) ===
    PRICING_EXPERIMENT_EXPOSURE_SOURCE
  );
}

const SESSION_VARIANT_KEY_PREFIX = "upsider_pricing_exp_variant";
const SESSION_PRICE_KEY_PREFIX = "upsider_pricing_exp_price_id";

function getSessionVariantKey(experimentId: string): string {
  return `${SESSION_VARIANT_KEY_PREFIX}:${experimentId}`;
}

function getSessionPriceKey(experimentId: string): string {
  return `${SESSION_PRICE_KEY_PREFIX}:${experimentId}`;
}

function getPinnedVariant(
  experimentId: string,
): { variant: string; priceId: string } | null {
  if (typeof window === "undefined") return null;
  try {
    const variant = sessionStorage.getItem(getSessionVariantKey(experimentId));
    const priceId = sessionStorage.getItem(getSessionPriceKey(experimentId));
    if (variant && priceId) return { variant, priceId };
  } catch {
  }
  return null;
}

function pinVariant(experimentId: string, variant: string, priceId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(getSessionVariantKey(experimentId), variant);
    sessionStorage.setItem(getSessionPriceKey(experimentId), priceId);
  } catch {
  }
}

export function resolveEvMonthlyPricingExperiment({
  controlPriceId,
  controlPrice,
  allowedPriceIds,
  featureFlagValue,
  featureFlagPayload,
  priceById,
  experimentId = EV_MONTHLY_PRICING_EXPERIMENT_ID,
  payloadPriceIdKey = PAYLOAD_PRICE_ID_KEY,
  pinScope = experimentId,
}: ResolveEvMonthlyPricingExperimentOptions): EvMonthlyPricingExperimentResolution {
  const normalizedControlPriceId = sanitizePricingExperimentValue(controlPriceId);
  if (!normalizedControlPriceId) {
    return {
      checkoutPriceId: controlPriceId,
      displayPrice: controlPrice,
      assignedVariant: null,
      metadata: null,
      fallbackReason: null,
    };
  }

  const allowedSet = new Set(
    Array.from(allowedPriceIds)
      .map((value) => sanitizePricingExperimentValue(value))
      .filter((value): value is string => Boolean(value)),
  );
  allowedSet.add(normalizedControlPriceId);

  let assignedVariant = normalizeVariant(featureFlagValue);
  if (!assignedVariant) {
    const flagWasEvaluated = featureFlagValue !== null && featureFlagValue !== undefined;
    const pinned = flagWasEvaluated ? getPinnedVariant(pinScope) : null;
    if (pinned && allowedSet.has(pinned.priceId)) {
      assignedVariant = pinned.variant;
    } else {
      return {
        checkoutPriceId: normalizedControlPriceId,
        displayPrice: controlPrice,
        assignedVariant: null,
        metadata: null,
        fallbackReason: null,
      };
    }
  }

  const pinned = getPinnedVariant(pinScope);
  if (pinned && pinned.variant !== assignedVariant && allowedSet.has(pinned.priceId)) {
    assignedVariant = pinned.variant;
  }

  const controlMetadata: PricingExperimentMetadata = {
    pricing_experiment_id: experimentId,
    pricing_experiment_variant: EV_MONTHLY_PRICING_CONTROL_VARIANT,
    pricing_experiment_price_id: normalizedControlPriceId,
  };

  if (assignedVariant === EV_MONTHLY_PRICING_CONTROL_VARIANT) {
    pinVariant(pinScope, assignedVariant, normalizedControlPriceId);
    return {
      checkoutPriceId: normalizedControlPriceId,
      displayPrice: controlPrice,
      assignedVariant,
      metadata: controlMetadata,
      fallbackReason: null,
    };
  }

  const payloadPriceId = pinned?.variant === assignedVariant && pinned?.priceId
    ? pinned.priceId
    : sanitizePricingExperimentValue(featureFlagPayload?.[payloadPriceIdKey]);
  if (!payloadPriceId) {
    return {
      checkoutPriceId: normalizedControlPriceId,
      displayPrice: controlPrice,
      assignedVariant,
      metadata: controlMetadata,
      fallbackReason: "missing_payload_price_id",
    };
  }

  if (!allowedSet.has(payloadPriceId)) {
    return {
      checkoutPriceId: normalizedControlPriceId,
      displayPrice: controlPrice,
      assignedVariant,
      metadata: controlMetadata,
      fallbackReason: "unknown_payload_price_id",
    };
  }

  pinVariant(pinScope, assignedVariant, payloadPriceId);

  const mappedDisplayPrice = resolvePriceFromMap(priceById, payloadPriceId);
  const displayPrice = mappedDisplayPrice ?? controlPrice;

  return {
    checkoutPriceId: payloadPriceId,
    displayPrice,
    assignedVariant,
    metadata: {
      pricing_experiment_id: experimentId,
      pricing_experiment_variant: assignedVariant,
      pricing_experiment_price_id: payloadPriceId,
    },
    fallbackReason: null,
  };
}
